import { cn } from '../../lib/utils'

interface SkeletonProps {
  className?: string
}

/**
 * A single shimmering placeholder block.
 *
 * Skeletons are laid out to match the shape of the content they stand in for,
 * so the page doesn't reflow when data lands. `motion-reduce:animate-none`
 * honours the OS "reduce motion" setting — a full-page pulse is exactly the
 * kind of thing that setting exists to stop.
 *
 * These are decorative: the surrounding `SkeletonScreen` owns the single
 * `role="status"` announcement, so screen readers hear "Loading" once rather
 * than once per block.
 */
export function Skeleton({ className }: SkeletonProps) {
  return (
    <div
      aria-hidden="true"
      className={cn(
        'animate-pulse rounded-md bg-muted-foreground/15 motion-reduce:animate-none',
        className,
      )}
    />
  )
}

/**
 * Wrapper that makes a block of skeletons legible to assistive tech. Renders
 * one polite live region; sighted users see the placeholder shapes.
 */
export function SkeletonScreen({
  children,
  label = 'Loading',
  className,
}: {
  children: React.ReactNode
  label?: string
  className?: string
}) {
  return (
    <div role="status" aria-busy="true" aria-live="polite" className={className}>
      <span className="sr-only">{label}</span>
      {children}
    </div>
  )
}

/** Several lines of body text, last line short like real prose. */
export function SkeletonText({ lines = 3, className }: { lines?: number; className?: string }) {
  return (
    <div className={cn('space-y-2', className)}>
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton key={i} className={cn('h-4', i === lines - 1 ? 'w-2/3' : 'w-full')} />
      ))}
    </div>
  )
}

/** Page title + subtitle, matching the `h1 + p` header every page uses. */
export function SkeletonPageHeader({ withAction = false }: { withAction?: boolean }) {
  return (
    <div className="mb-6 flex items-start justify-between gap-4">
      <div className="space-y-2">
        <Skeleton className="h-7 w-48" />
        <Skeleton className="h-4 w-72 max-w-full" />
      </div>
      {withAction && <Skeleton className="h-9 w-28 flex-shrink-0 rounded-lg" />}
    </div>
  )
}

/** One app/folder tile as used on Home, My Apps and Browse. */
export function SkeletonCard({ className }: SkeletonProps) {
  return (
    <div className={cn('rounded-lg border border-border bg-card p-4', className)}>
      <div className="flex items-start gap-3">
        <Skeleton className="h-10 w-10 flex-shrink-0 rounded-lg" />
        <div className="min-w-0 flex-1 space-y-2">
          <Skeleton className="h-5 w-1/2" />
          <Skeleton className="h-3 w-full" />
          <Skeleton className="h-3 w-4/5" />
        </div>
      </div>
      <div className="mt-4 flex items-center gap-2">
        <Skeleton className="h-5 w-16 rounded-full" />
        <Skeleton className="h-5 w-20 rounded-full" />
      </div>
    </div>
  )
}

/** Responsive grid of app tiles. */
export function SkeletonCardGrid({ count = 6, className }: { count?: number; className?: string }) {
  return (
    <div className={cn('grid gap-4 sm:grid-cols-2 lg:grid-cols-3', className)}>
      {Array.from({ length: count }).map((_, i) => (
        <SkeletonCard key={i} />
      ))}
    </div>
  )
}

/** Stacked rows — notification lists, activity feeds, comment threads. */
export function SkeletonList({
  count = 5,
  showAvatar = true,
  className,
}: {
  count?: number
  showAvatar?: boolean
  className?: string
}) {
  return (
    <div className={cn('space-y-2', className)}>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="flex items-start gap-3 rounded-lg border border-border bg-card p-4">
          {showAvatar && <Skeleton className="h-8 w-8 flex-shrink-0 rounded-full" />}
          <div className="min-w-0 flex-1 space-y-2">
            <Skeleton className="h-4 w-1/3" />
            <Skeleton className="h-3 w-full" />
          </div>
          <Skeleton className="h-3 w-12 flex-shrink-0" />
        </div>
      ))}
    </div>
  )
}

/** Header row + body rows for tabular admin/analytics screens. */
export function SkeletonTable({
  rows = 6,
  columns = 4,
  className,
}: {
  rows?: number
  columns?: number
  className?: string
}) {
  return (
    <div className={cn('overflow-hidden rounded-lg border border-border', className)}>
      <div className="flex gap-4 border-b border-border bg-secondary/40 px-4 py-3">
        {Array.from({ length: columns }).map((_, i) => (
          <Skeleton key={i} className="h-3 flex-1" />
        ))}
      </div>
      {Array.from({ length: rows }).map((_, r) => (
        <div key={r} className="flex gap-4 border-b border-border px-4 py-3 last:border-b-0">
          {Array.from({ length: columns }).map((_, c) => (
            <Skeleton key={c} className={cn('h-4 flex-1', c === 0 && 'max-w-[40%]')} />
          ))}
        </div>
      ))}
    </div>
  )
}

/** A row of KPI tiles. */
export function SkeletonStatCards({ count = 4, className }: { count?: number; className?: string }) {
  return (
    <div className={cn('grid gap-4 sm:grid-cols-2 lg:grid-cols-4', className)}>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="rounded-lg border border-border bg-card p-4">
          <Skeleton className="h-3 w-24" />
          <Skeleton className="mt-3 h-7 w-16" />
        </div>
      ))}
    </div>
  )
}

/** Long-form document body — guide pages, changelog entries, markdown panels. */
export function ContentSkeleton() {
  return (
    <SkeletonScreen className="space-y-4" label="Loading content">
      <Skeleton className="h-8 w-2/3" />
      <Skeleton className="h-4 w-1/4" />
      <div className="mt-6 space-y-2">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-3/4" />
      </div>
      <Skeleton className="mt-4 h-48 w-full" />
      <div className="space-y-2">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-5/6" />
      </div>
    </SkeletonScreen>
  )
}

export function ReleaseListSkeleton({ count = 3 }: { count?: number }) {
  return (
    <SkeletonScreen className="space-y-6" label="Loading releases">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="space-y-2 rounded-lg border border-border p-4">
          <div className="flex items-center gap-3">
            <Skeleton className="h-6 w-16 rounded-full" />
            <Skeleton className="h-5 w-1/3" />
          </div>
          <Skeleton className="h-4 w-2/3" />
          <Skeleton className="h-3 w-1/6" />
        </div>
      ))}
    </SkeletonScreen>
  )
}
